import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { requestTyped } from '@xdocs/ws-protocol';
import type { ErrorAlert, SubscriptionCancelInput, SubscriptionGetResult } from '@xdocs/schemas';
import { useAuth } from '../../../auth/AuthProvider.js';
import { useWs } from '../../../ws/WsProvider.js';

export interface UseSubscriptionResult {
  subscription: SubscriptionGetResult | undefined;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  cancel: (input: SubscriptionCancelInput) => Promise<void>;
  isCanceling: boolean;
}

/**
 * F20/F24 plan card + F46 cancel flow. Keyed under `['user', userId, ...]`
 * so the generic `user:{userId}` push-invalidation skeleton
 * (`ws/queryInvalidation.ts`) refetches this automatically whenever the
 * Paddle webhook flips `users.tier` (`billingTierChangedPush`).
 */
export function useSubscription(): UseSubscriptionResult {
  const { userId } = useAuth();
  const { wsClient, connectionState } = useWs();
  const queryClient = useQueryClient();
  const enabled = connectionState === 'connected' && userId !== null;
  const queryKey = useMemo(() => ['user', userId, 'billing', 'subscription'] as const, [userId]);

  const query = useQuery<SubscriptionGetResult, ErrorAlert>({
    queryKey,
    queryFn: () => requestTyped(wsClient, 'billing.subscription.get', undefined),
    enabled,
  });

  const cancelMutation = useMutation<SubscriptionGetResult, ErrorAlert, SubscriptionCancelInput>({
    mutationFn: (input) => requestTyped(wsClient, 'billing.subscription.cancel', input),
    onSuccess: (result) => queryClient.setQueryData(queryKey, result),
  });

  return {
    subscription: query.data,
    // `isPending`, NOT `isLoading`: while the socket is still connecting the
    // query is DISABLED, so `isLoading` (`isPending && isFetching`) reports
    // false with no data — and `PlanCard` then rendered the Free branch as
    // if loaded, letting an Upgrade click fire `billing.checkout.start`
    // straight into `errors:ws.not_connected` (observed e2e). Pending-
    // without-data during the boot/reconnect window IS a loading state.
    isLoading: query.isPending,
    isError: query.isError,
    onRetry: () => void query.refetch(),
    cancel: async (input) => void (await cancelMutation.mutateAsync(input)),
    isCanceling: cancelMutation.isPending,
  };
}
